import { Flex, HStack, Text, Box } from '@chakra-ui/react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    isLoading?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    hasNextPage?: boolean;
    /** Vertical padding around the control. Lower it for compact footers. */
    py?: number | string;
}

const Pagination = ({ currentPage, totalPages, onPageChange, isLoading = false, hasNextPage = false, py = 2 }: PaginationProps) => {
    if (totalPages <= 1 && !hasNextPage) return null;

    const isFirstPage = currentPage === 1;
    const isLastPage = !hasNextPage && currentPage >= totalPages;

    const handlePageChange = (newPage: number) => {
        if (isLoading) return;
        const maxPage = hasNextPage ? Math.max(totalPages, currentPage + 1) : totalPages;
        const clampedPage = Math.min(maxPage, Math.max(1, newPage));

        if (clampedPage !== currentPage) {
            onPageChange(clampedPage);
        }
    };

    const start = Math.max(1, currentPage - 1);
    const end = Math.min(totalPages, currentPage + 1);
    const visiblePages: Array<number> = [];
    for (let page = start; page <= end; page += 1) {
        visiblePages.push(page);
    }

    const showLeadingFirst = visiblePages[0] > 1;
    const showLeadingEllipsis = visiblePages[0] > 2;
    const showTrailingEllipsis = hasNextPage || visiblePages[visiblePages.length - 1] < totalPages - 1;

    const getPageButtonStyles = (isActive: boolean) => ({
        w: '28px',
        h: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'md',
        border: '1px solid',
        borderColor: isActive ? '#0071E3' : 'gray.300',
        bg: isActive ? '#0071E3' : 'white',
        color: isActive ? 'white' : 'gray.600',
        cursor: isActive ? 'default' : 'pointer',
        _hover: isActive ? {} : { bg: 'gray.50', borderColor: 'gray.400' },
        transition: 'all 0.15s',
        fontSize: 'xs',
        fontWeight: 'semibold',
        lineHeight: 1,
    });

    return (
        <Flex justify="center" align="center" py={py} w="100%">
            <HStack spacing={2}>
                <Box
                    as="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={isFirstPage || isLoading}
                    w="28px"
                    h="28px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="md"
                    border="1px solid"
                    borderColor={isFirstPage ? "gray.200" : "gray.300"}
                    bg="white"
                    color={isFirstPage ? "gray.300" : "gray.600"}
                    cursor={isFirstPage ? "not-allowed" : "pointer"}
                    _hover={isFirstPage ? {} : { bg: "gray.50", borderColor: "gray.400" }}
                    transition="all 0.15s"
                    fontSize="md"
                    fontWeight="bold"
                    lineHeight={1}
                >
                    ‹
                </Box>

                {showLeadingFirst && (
                    <Box
                        as="button"
                        onClick={() => handlePageChange(1)}
                        disabled={isLoading}
                        {...getPageButtonStyles(false)}
                    >
                        1
                    </Box>
                )}

                {showLeadingEllipsis && (
                    <Text fontSize="xs" color="gray.500" px={1} userSelect="none">
                        ...
                    </Text>
                )}

                {visiblePages.map((page) => {
                    const isActive = page === currentPage;
                    return (
                        <Box
                            key={page}
                            as="button"
                            onClick={() => handlePageChange(page)}
                            disabled={isLoading || isActive}
                            {...getPageButtonStyles(isActive)}
                        >
                            {page}
                        </Box>
                    );
                })}

                {showTrailingEllipsis && (
                    <Text fontSize="xs" color="gray.500" px={1} userSelect="none">
                        ...
                    </Text>
                )}

                <Box
                    as="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={isLastPage || isLoading}
                    w="28px"
                    h="28px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="md"
                    border="1px solid"
                    borderColor={isLastPage ? "gray.200" : "gray.300"}
                    bg="white"
                    color={isLastPage ? "gray.300" : "gray.600"}
                    cursor={isLastPage ? "not-allowed" : "pointer"}
                    _hover={isLastPage ? {} : { bg: "gray.50", borderColor: "gray.400" }}
                    transition="all 0.15s"
                    fontSize="md"
                    fontWeight="bold"
                    lineHeight={1}
                >
                    ›
                </Box>
            </HStack>
        </Flex>
    );
};

export default Pagination;

